import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  VStack,
  Stack,
  Text,
  Button,
  Select,
  Spacer,
  HStack,
  Badge,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import { ProgramInfo, Track } from '../../generated/graphqlModels';
import CoursePlan from './CoursePlan';
import { DownloadIcon } from '@chakra-ui/icons';
import CkeditorDisplay from './CkeditorDisplay';
import { AuthContext } from '../../context/AuthProvider';
import Tippy from '@tippyjs/react';
import { Link } from 'react-router-dom';

interface ProgramDetailProps extends ProgramInfo {}

const ProgramDetail = (props: ProgramDetailProps) => {
  const { description, tracks, scopeAndSequence, programUnitsDescription } =
    props;
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
  const [selectedTrackIndex, setSelectedTrackIndex] = useState<number>(-1);
  const user = useContext(AuthContext);

  // useEffect(() => {
  //   if (tracks && tracks.length && !selectedTrack) {
  //     setSelectedTrack(tracks[0]!);
  //     setSelectedTrackIndex(0);
  //   }
  // }, [tracks]);

  const durations =
    tracks?.map((t) => Math.round(t.estimatedDuration / 3600)) || [];

  return (
    <Stack gap={[16, 16, 20]} spacing={0}>
      <Stack direction={{ base: 'column', md: 'row' }} spacing={0} gap={4}>
        <VStack alignItems="flex-start" flex={1}>
          <Text fontSize="3xl" fontWeight="bold">
            Technical Skills
          </Text>
          <HStack gap={2} spacing={0} wrap={'wrap'}>
            {props.technicalSkills
              .split(',')
              .filter((t) => t.length)
              .map((t) => (
                <Badge
                  fontSize="0.9375rem"
                  textTransform="initial"
                  fontWeight="500"
                  background="#e34c82"
                  borderRadius="3xl"
                  py={[1, 2]}
                  px={[2, 4]}
                  color="white"
                >
                  {t.trim()}
                </Badge>
              ))}
          </HStack>
        </VStack>
        <VStack alignItems="flex-start" flex={1}>
          <Text as="h2" fontSize="3xl" fontWeight="bold">
            Professional Competences
          </Text>
          <HStack gap={2} spacing={0} wrap={'wrap'}>
            {props.professionalCompetences
              .split(',')
              .filter((t) => t.length)
              .map((t) => (
                <Badge
                  fontSize="0.9375rem"
                  textTransform="initial"
                  fontWeight="500"
                  background="#e34c82"
                  color="white"
                  borderRadius="3xl"
                  py={[1, 2]}
                  px={[2, 4]}
                >
                  {t.trim()}
                </Badge>
              ))}
          </HStack>
        </VStack>
      </Stack>
      <Stack direction="column" gap={4} spacing={0}>
        <Text fontSize="3xl" fontWeight="bold">
          Program Description
        </Text>
        <CkeditorDisplay
          flex={3}
          dangerouslySetInnerHTML={{ __html: description }}
        ></CkeditorDisplay>
      </Stack>
      {tracks && tracks.length > 0 && (
        <Stack gap={4} spacing={0}>
          <Text fontSize="3xl" fontWeight="bold">
            Explore program units
          </Text>
          {programUnitsDescription && (
            <CkeditorDisplay
              flex={3}
              dangerouslySetInnerHTML={{ __html: programUnitsDescription }}
            ></CkeditorDisplay>
          )}
          <Box>
            <Select
              placeholder="Select program variant"
              onChange={(e: any) => {
                const index = e.target.value;
                if (tracks && tracks[index]) {
                  setSelectedTrackIndex(index);
                  setSelectedTrack(tracks[index]!);
                }
              }}
            >
              {durations.map((duration, index) => {
                const track = tracks?.[index];
                return (
                  <option
                    key={index}
                    value={index}
                    selected={selectedTrackIndex === index}
                  >
                    {track?.name ? track.name : 'Course'} - {duration} hours
                  </option>
                );
              })}
            </Select>
            <CoursePlan
              units={
                selectedTrack
                  ? selectedTrack!.trackUnits.map((u) => {
                      return {
                        id: u.unit.id,
                        name: u.unit.name,
                        modules: u.trackModules.map((m) => ({
                          id: m.module.id,
                          name: m.module.name,
                        })),
                        programInfoDescription:
                          u.unit.programInfoDescription || '',
                      };
                    })
                  : []
              }
            />
          </Box>
        </Stack>
      )}
      {props.technologiesUsedInTheProgram ? (
        <VStack gap={4} spacing={0} align="start">
          <Text fontSize="3xl" fontWeight="bold">
            Technologies used in the program
          </Text>
          <Text>{props.technologiesUsedInTheProgram}</Text>
        </VStack>
      ) : null}
      <VStack gap={4} spacing={0} align="start">
        <Text fontSize="3xl" fontWeight="bold">
          Teacher experience
        </Text>
        {props.teacherExperience ? (
          <CkeditorDisplay
            flex={3}
            dangerouslySetInnerHTML={{ __html: props.teacherExperience }}
          ></CkeditorDisplay>
        ) : (
          <VStack justify="start" align="start">
            <Text>
              With many of us in Stemi being teachers themselves, our starting
              point in creating every program is tackling challenges in
              education and society, including overcoming obstacles teachers
              come across in today’s schooling systems.
            </Text>
            <Text>
              Therefore, all{' '}
              <strong>
                our programs are tailored to relieve teachers of stress,
                paperwork and class preparation
              </strong>{' '}
              as much as possible.
            </Text>
            <Text>
              With our fully prepared materials and in depth teacher’s guides,
              for which{' '}
              <strong>
                teachers do not require prior knowledge and skills
              </strong>
              , we enable them to save time and spend it more actively with the
              students individually.
            </Text>
            <Text>
              <strong>Teacher’s materials include:</strong>
              <UnorderedList style={{ marginLeft: '32px' }}>
                <ListItem>
                  100% prepared in depth teacher’s guide for every unit
                </ListItem>
                <ListItem>
                  module courseware with notes, made for teacher-led learning
                </ListItem>
                <ListItem>
                  detailed implementation guide, made for student-led learning
                </ListItem>
              </UnorderedList>
            </Text>
            <Text>
              Our programs encourage teachers to{' '}
              <strong>“lecture less and mentor more”</strong>, helping students
              to cooperate and develop social skills which they need for both
              work and life in general. This way students explore and experience
              first hand what it takes to create full cycle products, mimicking
              real working environments.
            </Text>
            <Text>
              Utilizing these learning methods, mentors directly prepare
              students for real situations they are going to face once they land
              a first job or decide to launch their own startup.
            </Text>
            <Text>
              The way we see it, it’s a win-win situation where teachers fulfill
              their purpose, while preparing young people for realistic
              environments.
            </Text>
          </VStack>
        )}
      </VStack>
    </Stack>
  );
};

export default ProgramDetail;
